<template>
  <div class="safety-report-container">
    <!-- 页面标题 -->
    <!-- 报告配置区域 -->
    <el-card class="config-card" shadow="never">
      <el-form :model="reportConfig" label-width="120px" size="small">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="报告周期">
              <el-date-picker
                v-model="reportConfig.month"
                type="month"
                placeholder="选择月份"
                format="yyyy年MM月"
                value-format="yyyy-MM"
                @change="handleConfigChange"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="报告范围">
              <el-radio-group v-model="reportConfig.reportScope" @change="handleReportScopeChange">
                <el-radio label="全部部门">全部部门</el-radio>
                <el-radio label="选择部门">选择部门</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="8" v-if="reportConfig.reportScope === '选择部门'">
            <el-form-item label="选择部门">
              <el-select
                v-model="reportConfig.deptIds"
                multiple
                clearable
                placeholder="请选择部门"
                style="width: 100%"
                @change="handleDeptSelectChange"
              >
                <el-option
                  v-for="item in deptSelectOptions"
                  :key="item.id"
                  :label="item.deptName"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <el-form-item label="包含内容">
              <el-checkbox-group v-model="reportConfig.contentTypes" @change="handleContentChange">
                <el-checkbox label="checkStatistics">检查统计</el-checkbox>
                <el-checkbox label="hazardAnalysis">隐患分析</el-checkbox>
                <el-checkbox label="rectificationStatus">整改情况</el-checkbox>
                <el-checkbox label="accidentStatistics">事故统计</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- KPI指标卡片区域 -->
    <el-row :gutter="20" class="kpi-row" v-loading="loading">
      <el-col :span="6" v-if="showContent('checkStatistics')">
        <el-card class="kpi-card" shadow="hover">
          <div class="kpi-content">
            <div class="kpi-label">检查项数量</div>
            <div class="kpi-value">{{ summaryData.checkItemCount || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6" v-if="showContent('hazardAnalysis')">
        <el-card class="kpi-card" shadow="hover">
          <div class="kpi-content">
            <div class="kpi-label">隐患数量</div>
            <div class="kpi-value">{{ summaryData.hazardCount || 0 }}<span class="kpi-unit">项</span></div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6" v-if="showContent('rectificationStatus')">
        <el-card class="kpi-card" shadow="hover">
          <div class="kpi-content">
            <div class="kpi-label">整改完成率</div>
            <div class="kpi-value">{{ formatPercent(summaryData.completionRate) }}<span class="kpi-unit">%</span></div>
            <div class="kpi-subtitle">已完成整改/总隐患数</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6" v-if="showContent('accidentStatistics')">
        <el-card class="kpi-card" shadow="hover">
          <div class="kpi-content">
            <div class="kpi-label">事故统计</div>
            <div class="kpi-value">{{ summaryData.accidentCount || 0 }}<span class="kpi-unit">起</span></div>
            <div class="kpi-subtitle">本期事故数量及趋势</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 安全检查数据概览 -->
    <el-row :gutter="20" class="chart-row">
      <el-col :span="12">
        <el-card class="chart-card" shadow="never" v-loading="chartLoading.dept">
          <div slot="header" class="card-header">
            <span>安全检查数据概览</span>
          </div>
          <dept-bar-chart v-if="deptStatsData && deptStatsData.length > 0" :dept-stats="deptStatsData" />
          <div v-else class="empty-chart">暂无数据</div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="chart-card" shadow="never" v-loading="chartLoading.type">
          <div slot="header" class="card-header">
            <span>检查类型统计</span>
          </div>
          <type-ring-chart v-if="typeStatsData && typeStatsData.length > 0" :type-stats="typeStatsData" />
          <div v-else class="empty-chart">暂无数据</div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getSafetyReportSummary, getDeptStats, getTypeStats, getDeptTree, exportSafetyReportPdf } from './api'
import DeptBarChart from './components/DeptBarChart.vue'
import TypeRingChart from './components/TypeRingChart.vue'

export default {
  name: 'SafetyReport',
  components: {
    DeptBarChart,
    TypeRingChart
  },
  data() {
    return {
      loading: false,
      chartLoading: {
        dept: false,
        type: false
      },
      reportConfig: {
        month: this.getCurrentMonth(), // 当前月份，格式：2023-05
        reportScope: '全部部门',
        deptIds: [],
        contentTypes: ['checkStatistics', 'hazardAnalysis', 'rectificationStatus', 'accidentStatistics']
      },
      summaryData: {
        checkItemCount: 0,
        hazardCount: 0,
        completedRectifyCount: 0,
        completionRate: 0,
        accidentCount: 0
      },
      deptStatsData: [],
      typeStatsData: [],
      deptSelectOptions: [],
      exportLoading: false
    }
  },
  async mounted() {
    await this.loadDeptTree()
    this.handleConfigChange()
  },
  methods: {
    // 获取当前月份
    getCurrentMonth() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },

    // 加载部门树
    async loadDeptTree() {
      try {
        const res = await getDeptTree()
        let rawData = []
        if (res && Array.isArray(res.data)) {
          rawData = res.data
        } else if (res && res.data && Array.isArray(res.data.data)) {
          rawData = res.data.data
        }
        if (rawData.length) {
          this.deptSelectOptions = this.flattenDeptTree(rawData)
        } else {
          console.warn('部门数据为空，响应：', res && res.data)
          this.deptSelectOptions = []
        }
      } catch (error) {
        console.error('加载部门树失败：', error)
        this.$message.error('获取部门数据失败')
      }
    },

    // 加载报告数据（KPI指标）
    async loadReportData() {
      this.loading = true
      try {
        const params = {
          month: this.reportConfig.month,
          reportScope: this.reportConfig.reportScope,
          deptIds: this.reportConfig.reportScope === '选择部门' ? this.reportConfig.deptIds : null
        }
        const res = await getSafetyReportSummary(params)
        if (res.data.success && res.data.data) {
          this.summaryData = res.data.data
        } else {
          this.$message.warning(res.data.msg || '加载数据失败')
        }
      } catch (error) {
        this.$message.error('加载数据失败：' + (error.message || '未知错误'))
        console.error('加载报告数据失败：', error)
      } finally {
        this.loading = false
      }
    },

    // 加载部门统计数据（柱形图）
    async loadDeptStats() {
      this.chartLoading.dept = true
      try {
        const params = {
          month: this.reportConfig.month,
          reportScope: this.reportConfig.reportScope,
          deptIds: this.reportConfig.reportScope === '选择部门' ? this.reportConfig.deptIds : null
        }
        const res = await getDeptStats(params)
        if (res.data.success && res.data.data) {
          this.deptStatsData = res.data.data
        } else {
          this.$message.warning(res.data.msg || '加载部门统计数据失败')
        }
      } catch (error) {
        this.$message.error('加载部门统计数据失败：' + (error.message || '未知错误'))
        console.error('加载部门统计数据失败：', error)
      } finally {
        this.chartLoading.dept = false
      }
    },

    // 加载检查类型统计数据（环形图）
    async loadTypeStats() {
      this.chartLoading.type = true
      try {
        const params = {
          month: this.reportConfig.month,
          reportScope: this.reportConfig.reportScope,
          deptIds: this.reportConfig.reportScope === '选择部门' ? this.reportConfig.deptIds : null
        }
        const res = await getTypeStats(params)
        if (res.data.success && res.data.data) {
          this.typeStatsData = res.data.data
        } else {
          this.$message.warning(res.data.msg || '加载类型统计数据失败')
        }
      } catch (error) {
        this.$message.error('加载类型统计数据失败：' + (error.message || '未知错误'))
        console.error('加载类型统计数据失败：', error)
      } finally {
        this.chartLoading.type = false
      }
    },

    // 配置变更处理
    handleConfigChange() {
      // 同时加载所有数据
      this.loadReportData()
      this.loadDeptStats()
      this.loadTypeStats()
    },

    // 报告范围变更处理
    handleReportScopeChange() {
      if (this.reportConfig.reportScope === '全部部门') {
        this.reportConfig.deptIds = []
      }
      this.handleConfigChange()
    },

    // 部门选择变更处理
    handleDeptSelectChange() {
      this.handleConfigChange()
    },

    // 内容选择变更处理（前端控制显示/隐藏，无需重新请求）
    handleContentChange() {
      // 仅控制显示/隐藏，不重新请求数据
    },

    // 判断是否显示内容
    showContent(type) {
      return this.reportConfig.contentTypes.includes(type)
    },

    // 格式化百分比
    formatPercent(value) {
      if (value == null || value === undefined) {
        return '0.00'
      }
      return Number(value).toFixed(2)
    },

    // 扁平化部门树，供下拉选择使用（仅显示末级部门）
    flattenDeptTree(treeData = []) {
      const result = []
      const traverse = (nodes) => {
        nodes.forEach((node) => {
          const currentName = this.getDeptDisplayName(node)
          result.push({
            id: String(node.id),
            deptName: currentName
          })
          if (node.children && node.children.length > 0) {
            traverse(node.children)
          }
        })
      }
      traverse(treeData)
      return result
    },

    // 获取部门显示名称
    getDeptDisplayName(node = {}) {
      return (
        node.deptName ||
        node.deptFullName ||
        node.fullName ||
        node.name ||
        node.title ||
        node.dictLabel ||
        node.label ||
        node.deptCode ||
        String(node.id || '')
      )
    },
  }
}
</script>

<style scoped lang="scss">
.safety-report-container {
  padding: 12px;
  background-color: #FFFFFF;
}

.page-header {
  margin-bottom: 20px;
  h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    color: #303133;
  }
}

.config-card {
  margin-bottom: 20px;

  .card-header {
    font-weight: 500;
    font-size: 16px;
    color: #303133;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .config-actions {
      display: flex;
      gap: 8px;
    }
  }
}

.kpi-row {
  margin-bottom: 20px;

  .kpi-card {
    height: 150px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .kpi-content {
      text-align: center;
      padding: 10px 0;

      .kpi-label {
        font-size: 14px;
        color: #909399;
        margin-bottom: 15px;
      }

      .kpi-value {
        font-size: 36px;
        font-weight: bold;
        color: #409EFF;
        margin-bottom: 10px;

        .kpi-unit {
          font-size: 18px;
          font-weight: normal;
          margin-left: 5px;
        }
      }

      .kpi-subtitle {
        font-size: 12px;
        color: #C0C4CC;
        margin-top: 5px;
      }
    }
  }
}

.chart-row {
  .chart-card {
    min-height: 450px;

    .card-header {
      font-weight: 500;
      font-size: 16px;
      color: #303133;
    }

    .empty-chart {
      height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #909399;
      font-size: 14px;
    }
  }
}
</style>

